import { playCinematic, SENDA_VIDEOS } from './cinematics.ts';
import type * as THREE from 'three';
import { SENDA, sendaChapters, type SendaAct, type SendaChapter, type SendaCine, type SendaDef, type SendaFight } from '@somnhold/content';
import { chapterLevel, condMet, heroLook, heroName, sendaAct, sendaDef, sendaLines, sendaOf, sendaQuestions, type BattleState, type CampaignCommand, type PlayerState, type SendaState } from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { Cinematic, type CineLine } from '../game/cine.ts';
import { getLang, t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { sendaFace, triesMarks, whoKey } from './senda-panel.ts';
import type { CampaignService } from './service.ts';

/**
 * Walking a path (docs/SENDAS.md): its scenes, the campfire and the decision on the engine's stage (game/cine.ts),
 * one after another as the story goes, until a fight or the chapter's end; the fight's result card after it.
 */

export interface SendaDeps {
  svc: CampaignService;
  renderer: THREE.WebGLRenderer;
  /** Puts a full screen up (the one before is destroyed). */
  show: (el: HTMLElement, owner: { destroy(): void }) => void;
  /** Back to the bastion, the path's card open. */
  back: () => void;
  /** The path's next fight (`again`: in a chapter played again). */
  fight: (again: boolean) => void;
  cmd: (c: CampaignCommand) => Promise<boolean>;
}

const me = (svc: CampaignService): PlayerState => svc.state.players.find((p) => p.id === svc.me) as PlayerState;

/** A scene without its own cinematic: dusk over a clearing, the people who speak in it facing the hero. */
const plainCine = (who: string[]): SendaCine => ({
  place: 'camp',
  sky: 'dusk',
  cam: [
    { from: [-3, 5, 2.6], at: [0, 0, 0.6], ms: 7000 },
    { from: [2, 3, 1.6], at: [0, 0, 0.7], ms: 7000 },
  ],
  figures: [{ who: 'hero', q: 1, r: -1, facing: 3, pose: 'stand' }, ...who.slice(0, 3).map((w, i) => ({ who: w, q: -1 + i, r: i === 1 ? 0 : 1 - i, facing: 0, pose: 'stand' as const }))],
});

/** Who speaks in an act (beside the narrator and the hero). */
const speakers = (a: SendaAct): string[] => {
  const all = a.kind === 'fire' ? [...a.with, ...a.questions.flatMap((q) => q.lines.map((l) => l.who))] : a.kind === 'fight' ? [] : a.lines.map((l) => l.who);
  return [...new Set(all.filter((w) => w !== 'narrator' && w !== 'hero'))];
};

/** A line on stage: its speaker's name and face, and its text. */
const lineOf = (c: Cinematic, def: SendaDef, p: PlayerState, base: string, l: { id: string; who: string; light?: number; cue?: string }): CineLine => ({
  name: l.who === 'narrator' ? '' : l.who === 'hero' ? heroName(p) : t(`senda.${def.id}.npc.${l.who}`),
  text: t(`${base}.${l.id}`),
  face: l.who === 'narrator' ? null : c.face(l.who),
  ...(l.light ? { light: l.light } : {}),
  ...(l.cue ? { cue: l.cue } : {}),
});

/** The act the hero is at, on stage (a fight goes to the board instead). */
export const playSenda = (d: SendaDeps, again = !!sendaOf(me(d.svc).hero)?.again): void => {
  const p = me(d.svc);
  const hero = p.hero;
  const def = sendaDef(hero);
  const at = sendaAct(hero, d.svc.now(), again);
  if (!def || !at) return d.back();
  if (at.def.kind === 'fight') return d.fight(again);
  const act = at.def;
  const ch = def.chapters[at.ch] as SendaChapter;
  const base = `senda.${def.id}.${ch.id}.${act.id}`;
  // Where the scene stands: its own place, or (a building the bastion lacks, the paladin's Temple) its stand-in.
  const own = act.cine ?? plainCine(speakers(act));
  const built = (type: string): boolean => Object.values(d.svc.state.cells).some((c) => c.built?.structure === type);
  // Who stands in the scene, and where, can depend on the story (the warrior's chapter 5: Wenn up there, or Dalmo by the
  // fire; the druid's: the Grandsire standing, or lying down over the wood).
  const flags = { aff: {}, flags: (sendaOf(hero) as SendaState | null)?.flags ?? [] };
  const told = own.swap && condMet(flags, own.swap.if) ? { ...own.swap.cine, ...(own.video ? { video: own.video } : {}) } : own;
  const raw = told.alt && !built(told.alt.unless) ? { ...told.alt.cine, ...(told.video ? { video: told.video } : {}) } : told;
  const cine = raw.figures?.some((f) => f.if) ? { ...raw, figures: raw.figures.filter((f) => condMet(flags, f.if)) } : raw;
  const kicker = `${t(`senda.${def.id}.title`)} · ${t('senda.ui.chapter', { n: at.ch + 1, of: sendaChapters(def), title: t(`senda.${def.id}.${ch.id}.title`) })}`;
  const title = act.kind === 'fire' ? t('senda.fire.title') : act.kind === 'choice' ? t('senda.ui.kind.choice') : t(`senda.${def.id}.${ch.id}.title`);
  // A scene with a video made for it (and no lines over it): the video; else the engine's scene.
  const video = act.kind === 'scene' && !act.lines.length && act.cine?.video ? SENDA_VIDEOS[act.cine.video] : undefined;
  if (video && act.cine?.video) {
    const id = act.cine.video;
    playCinematic(
      { id, src: video, poster: video.replace(/\.mp4$/, '.jpg'), kicker: `senda.${def.id}.title`, title: `senda.${def.id}.${ch.id}.title`, lines: [], due: () => false },
      () => void d.cmd({ t: 'sendaGo', ch: at.ch, act: at.act }).then((ok) => (ok ? playSenda(d, again) : d.back())),
    );
    return;
  }
  const stage = new Cinematic(d.renderer, { cine, hero: { pid: p.id, name: heroName(p), cls: hero.heroClass, level: hero.level, ...(heroLook(hero) ? { look: heroLook(hero) as string } : {}) }, cls: def.cls, kicker, title }, (who) => sendaFace(d.renderer, whoKey(hero, def.cls, who)));
  d.show(stage.el, stage);
  stage.start();
  const from = { ch: at.ch, act: at.act, was: sendaOf(hero) as SendaState };
  const fromCh = from.was.ch;
  const after = (): void => after_(d, stage, def, fromCh, again);
  if (act.kind === 'scene') {
    stage.tell(
      sendaLines(hero, act.lines, d.svc.state).map((l) => lineOf(stage, def, p, base, l)),
      () => void d.cmd({ t: 'sendaGo', ch: at.ch, act: at.act }).then((ok) => (ok ? after() : d.back())),
      true,
    );
    return;
  }
  if (act.kind === 'choice') {
    const lines = sendaLines(hero, act.lines, d.svc.state).map((l) => lineOf(stage, def, p, base, l));
    const flags = (sendaOf(hero) as SendaState).flags ?? [];
    const options = (): void => {
      if (again) {
        // Played again, the decision stands: what was chosen, and on.
        const chosen = act.options.find((o) => o.set.every((f) => flags.includes(f)));
        stage.ask(
          t('senda.choice.made'),
          '',
          act.options.map((o) => ({ label: t(`${base}.c.${o.id}`), desc: t(`${base}.c.${o.id}.desc`), done: o !== chosen, test: `senda-choice-${o.id}`, run: () => undefined })),
          [h('div.cine-actions', {}, h('button.btn', { 'data-test': 'senda-choice-on', onclick: () => void d.cmd({ t: 'sendaGo', ch: at.ch, act: at.act }).then(() => after()) }, t('senda.next.on')))],
        );
        return;
      }
      stage.ask(
        t('senda.choice.title'),
        t('senda.choice.note'),
        act.options.map((o) => ({
          label: t(`${base}.c.${o.id}`),
          desc: t(`${base}.c.${o.id}.desc`),
          test: `senda-choice-${o.id}`,
          run: () => void d.cmd({ t: 'sendaChoice', ch: at.ch, act: at.act, choice: o.id }).then((ok) => (ok ? after() : d.back())),
        })),
      );
    };
    stage.tell(lines, options);
    return;
  }
  // The campfire: three questions, two asked; each answer told, then back to the fire.
  const fire = (): void => {
    const st = sendaOf(hero) as SendaState;
    const still = sendaAct(hero, d.svc.now(), again);
    // Two asked: the sim moved on to the next act.
    if (!still || still.ch !== at.ch || still.act !== at.act) return after();
    const asked = st.asked ?? [];
    const open = sendaQuestions(hero, act.questions, d.svc.state).map((q) => q.id);
    const n = asked.length;
    // (A question that needs a mark the hero's campaign hasn't got isn't at the fire: Ragna, before El Cuerno's end.)
    const qs = act.questions.filter((q) => !q.if || open.includes(q.id) || asked.includes(q.id));
    stage.ask(
      t('senda.fire.title'),
      t('senda.fire.note', { n, of: Math.min(SENDA.asks, qs.length) }),
      qs.map((q) => ({
        label: t(`${base}.q.${q.id}`),
        done: !open.includes(q.id),
        test: `senda-ask-${q.id}`,
        run: () =>
          void d.cmd({ t: 'sendaAsk', ch: at.ch, act: at.act, q: q.id }).then((ok) => {
            if (!ok) return d.back();
            stage.clearUnder();
            stage.tell(
              sendaLines(hero, q.lines, d.svc.state).map((l) => lineOf(stage, def, p, `${base}.${q.id}`, l)),
              fire,
            );
          }),
      })),
    );
  };
  stage.tell([], fire);
};

/** After an act on stage: the next scene, what comes next (a fight), or the chapter's end. */
const after_ = (d: SendaDeps, stage: Cinematic, def: SendaDef, fromCh: number, again: boolean): void => {
  const p = me(d.svc);
  const st = sendaOf(p.hero) as SendaState;
  // A chapter played again told to its end, or the path's chapter done.
  if ((again && !st.again) || (!again && st.ch > fromCh)) return stage.card(doneCard(d, def, fromCh, again, () => stage.close(d.back)));
  const next = sendaAct(p.hero, d.svc.now(), again);
  if (!next) return stage.close(d.back);
  if (next.def.kind !== 'fight') return stage.close(() => playSenda(d, again));
  stage.card(nextCard(d, def, next.ch, next.def, st, again, stage));
};

/** What comes next on stage: the fight, its rules and the tries, and the way in (or later). */
const nextCard = (d: SendaDeps, def: SendaDef, ch: number, act: SendaFight, st: SendaState, again: boolean, stage: Cinematic): HTMLElement => {
  const c = def.chapters[ch] as SendaChapter;
  const base = `senda.${def.id}.${c.id}.${act.id}`;
  return h(
    'div.cine-card',
    { 'data-test': 'senda-next' },
    h('span.cine-card-kicker', {}, act.trial ? t('senda.ui.kind.trial') : t('senda.next.title')),
    h('h3', {}, t(`${base}.title`)),
    h('p', {}, t(`${base}.goal`)),
    h('p.small.cine-lose', {}, h('span', { html: ICONS.skull }), t('senda.hud.lose', { text: t(`${base}.lose`) })),
    again ? h('p.small', {}, t('senda.result.again')) : triesMarks(d.svc.state, st, true),
    h(
      'div.cine-actions',
      {},
      h('button.btn.ghost', { 'data-test': 'senda-later', onclick: () => stage.close(d.back) }, t('senda.next.later')),
      h('button.btn', { 'data-test': 'senda-fight', onclick: () => (audio.play('click'), stage.close(() => d.fight(again))) }, t('senda.next.go')),
    ),
  );
};

/** A chapter closed: its experience and its piece, and when the next opens. */
const doneCard = (d: SendaDeps, def: SendaDef, ch: number, again: boolean, back: () => void): HTMLElement => {
  const c = def.chapters[ch] as SendaChapter;
  const xp = c.xp ?? SENDA.xp[ch] ?? 0;
  // The path's end by its design (a path written up to chapter 1 still has four to come).
  const last = ch >= sendaChapters(def) - 1;
  audio.play('victory', 0.5);
  return h(
    'div.cine-card.done',
    { 'data-test': 'senda-done' },
    h('span.cine-card-kicker', {}, t('senda.done.title')),
    h('h3', {}, t(`senda.${def.id}.${c.id}.title`)),
    again
      ? h('p', {}, t('senda.done.again'))
      : h(
          'div.cine-gains',
          {},
          h('span.cine-gain', {}, h('span', { html: ICONS.sparkle }), t('senda.done.xp', { xp: xp.toLocaleString(getLang() === 'es' ? 'es-AR' : 'en-US') })),
          c.piece ? h('span.cine-gain', {}, h('span', { html: ICONS.armor }), t('senda.done.piece', { piece: t(`senda.${def.id}.${c.id}.piece`) })) : '',
        ),
    again ? '' : h('p.small', {}, last ? t('senda.done.over') : t('senda.done.next', { n: ch + 2, l: chapterLevel(def, ch + 1) })),
    h('div.cine-actions', {}, h('button.btn', { 'data-test': 'senda-done-back', onclick: back }, t('senda.done.back'))),
  );
};

/** How a path's fight ended: won (and on), or lost (the tries left, a friend's coming in, or the path closed). */
export const sendaResult = (d: SendaDeps, b: BattleState, title: string, before: SendaState, again: boolean, onBack: () => void, onOn: () => void, onRetry: () => void): HTMLElement => {
  const p = me(d.svc);
  const def = sendaDef(p.hero) as SendaDef;
  const st = sendaOf(p.hero) as SendaState;
  const won = b.result === 'won';
  const ch = def.chapters[before.ch] as SendaChapter;
  const closed = !again && !won && !!st.closed;
  // A friend's try coming in: one more used than before, from this chapter.
  const usedBefore = (before.gifts ?? []).filter((g) => g.ch === before.ch && g.used).length;
  const nowUsed = (st.gifts ?? []).filter((g) => g.ch === before.ch && g.used);
  const gift = !again && !won && nowUsed.length > usedBefore ? nowUsed[nowUsed.length - 1] : undefined;
  const giver = gift ? d.svc.state.players.find((x) => x.id === gift.pid) : undefined;
  const face = closed ? sendaFace(d.renderer, whoKey(p.hero, def.cls, ch.closedBy)) : null;
  const el: HTMLElement = h(
    'div.modal-back.saga-end.senda-end',
    { 'data-test': 'senda-result' },
    h(
      'div.modal',
      { role: 'dialog', 'aria-modal': 'true' },
      h('header', {}, h('h2', {}, won ? t('senda.result.won') : t('senda.result.lost')), h('p', {}, title)),
      h(
        'div.body',
        {},
        again
          ? h('p', {}, t('senda.result.again'))
          : won
            ? wonNext(d, def, again)
            : closed
              ? h(
                  'div',
                  {},
                  h('figure.sd-quote', {}, face ? h('img.sd-quote-face', { src: face, alt: '' }) : h('span.sd-quote-face'), h('blockquote', {}, h('p', {}, `«${t(`senda.${def.id}.${ch.id}.closed`)}»`), h('cite', {}, t(`senda.${def.id}.npc.${ch.closedBy}`)))),
                  h('p', {}, t('senda.result.closed')),
                )
              : h('div', {}, gift ? h('p', {}, t('senda.result.gift', { name: giver ? heroName(giver) : '—' })) : h('p', {}, st.left === 1 ? t('senda.result.leftOne') : t('senda.result.left', { n: st.left })), triesMarks(d.svc.state, st, true)),
      ),
      h(
        'footer',
        {},
        won
          ? h('button.btn', { 'data-test': 'senda-result-on', onclick: () => (el.remove(), onOn()) }, t('senda.result.on'))
          : h(
              'div.saga-go',
              {},
              h('button.btn.ghost', { 'data-test': 'senda-result-back', onclick: () => (el.remove(), onBack()) }, t('senda.result.back')),
              closed ? '' : h('button.btn', { 'data-test': 'senda-result-retry', onclick: () => (el.remove(), onRetry()) }, t('senda.result.retry')),
            ),
      ),
    ),
  );
  return el;
};

/** After a fight won: who waits in the next scene, or the next fight's name. */
const wonNext = (d: SendaDeps, def: SendaDef, again: boolean): HTMLElement => {
  const p = me(d.svc);
  const at = sendaAct(p.hero, d.svc.now(), again);
  const ch = at ? def.chapters[at.ch] : undefined;
  if (!at || !ch) return h('p', {}, t('senda.ui.goes'));
  if (at.def.kind === 'fight') return h('p', {}, h('b', {}, t('senda.next.title')), ' · ', t(`senda.${def.id}.${ch.id}.${at.def.id}.title`));
  const who = speakers(at.def)[0];
  return h('p', {}, who ? t('senda.ui.waits', { who: t(`senda.${def.id}.npc.${who}`) }) : t('senda.ui.goes'));
};

/** The fight's names and keys for its HUD: the act's text prefix and who is who. */
export const sendaFightInfo = (svc: CampaignService, again: boolean): { key: string; names: Record<string, string>; title: string; subtitle: string; lose: string } | null => {
  const p = me(svc);
  const def = sendaDef(p.hero);
  const at = sendaAct(p.hero, svc.now(), again);
  if (!def || !at || at.def.kind !== 'fight') return null;
  const ch = def.chapters[at.ch] as SendaChapter;
  const key = `senda.${def.id}.${ch.id}.${at.def.id}`;
  const names: Record<string, string> = { hero: heroName(p) };
  for (const n of at.def.npcs ?? []) names[n.id] = t(`senda.${def.id}.npc.${n.id}`);
  for (const l of at.def.says ?? []) if (l.who !== 'hero') names[l.who] = t(`senda.${def.id}.npc.${l.who}`);
  if (at.def.memory) names[at.def.memory] = t(`senda.${def.id}.face.${at.def.memory}`);
  return { key, names, title: t(`${key}.title`), subtitle: `${t(`senda.${def.id}.title`)} · ${t('senda.ui.chapter', { n: at.ch + 1, of: sendaChapters(def), title: t(`senda.${def.id}.${ch.id}.title`) })}`, lose: t(`${key}.lose`) };
};
